import { DEFAULT_COUNTRY_CODE_BY_LANGUAGE, SupportedLanguageCode } from '@cineamo/api/types/AppLocale';

import { Locale } from '../constants/locale-constants';

import { LowerLetter, UpperLetter } from './string-helper';
import { isLocale, toISO_639_1, toISO_3166_1 } from './type-guards';

export {
    APP_LOCALES,
    DEFAULT_COUNTRY_CODE_BY_LANGUAGE,
    getCountryCodeFromAppLocale,
    getLanguageCodeFromAppLocale,
    isAppLocale
} from '@cineamo/api/types/AppLocale';
export type { AppLocale, SupportedLanguageCode } from '@cineamo/api/types/AppLocale';
export { SUPPORTED_COUNTRY_CODES, isSupportedCountryCode } from '@cineamo/api/types/CountryCode';
export type { SupportedCountryCode } from '@cineamo/api/types/CountryCode';
export type ISO_639_3_LOCALE = `${LowerLetter}${LowerLetter}${LowerLetter}`; // gsw
export type ISO_639_1_LOCALE = `${LowerLetter}${LowerLetter}`; // de
export type ISO_3166_1_LOCALE = `${UpperLetter}${UpperLetter}`; // Country code like CH
export type ISO_3166_2_LOCALE = `${Uppercase<string>}${Uppercase<string>}-${Uppercase<string>}${Uppercase<string>}`; // CH-ZH
export type LanguageCode = `${LowerLetter}${LowerLetter}`; // de
export type CountryCode = `${UpperLetter}${UpperLetter}`; // CH
export type TimeZone = `${string}/${string}`; // Europe/Berlin
/**
 * Returns the language 'code' for a given locale.
 * @example
 * locale: 'en-GB'
 * returns: 'en'
 * @param locale
 */
export function getLanguageCodeFromLocale(locale: Locale): LanguageCode {
    return locale.replace('_', '-').substring(0, 2) as LanguageCode;
}

/**
 * Returns the country 'code' for a given locale.
 * @example
 * locale: 'en_GB'
 * returns: 'GB'
 * @param locale
 */
export function getCountryCodeFromLocale(locale: Locale): CountryCode {
    return locale.split('_')?.[1] as CountryCode;
}

/**
 * Returns the locale from a language string.
 * @example
 * language: 'en' or 'en_GB
 * returns: 'en_GB'
 * @param language
 * @param defaultLocale
 */
export function getLocaleFromLanguage(language: string | undefined, defaultLocale: string = Locale.en_GB): Locale {
    if (!language) {
        return Locale[defaultLocale as keyof typeof Locale];
    }
    return language.length === 2 && Locale[`${language}_${language.toUpperCase()}` as keyof typeof Locale]
        ? Locale[`${language}_${language.toUpperCase()}` as keyof typeof Locale]
        : Locale[language.replace('-', '_') as keyof typeof Locale]
          ? Locale[language.replace('-', '_') as keyof typeof Locale]
          : Locale[Object.keys(Locale).find((locale) => locale.startsWith(language)) as keyof typeof Locale]
            ? Locale[Object.keys(Locale).find((locale) => locale.startsWith(language)) as keyof typeof Locale]
            : Locale[defaultLocale as keyof typeof Locale];
}

/**
 * Returns the locale as a string.
 * @example
 * locale: 'en_GB'
 * returns: 'en-EN'
 * @param locale
 */
export function getLocaleStringFromLocale(locale: Locale): string {
    return locale.replace('_', '-');
}

/**
 * Returns the name of the language for a given locale.
 * @example
 * locale: 'en-GB'
 * returns: 'British English'
 * @param localeToTranslate
 * @param locale
 */
export function getLanguageNameFromLocale(localeToTranslate: string, locale?: string): string | undefined {
    if (!localeToTranslate) {
        return undefined;
    }
    const currentLocale = locale || 'de';
    return new Intl.DisplayNames(currentLocale.replace('_', '-'), { type: 'language' }).of(
        localeToTranslate.substring(0, 2)
    );
}

export const LOCALE_MAPPING: { [key in ISO_639_3_LOCALE]?: ISO_639_1_LOCALE } = {
    aar: 'aa',
    abk: 'ab',
    afr: 'af',
    aka: 'ak',
    amh: 'am',
    ara: 'ar',
    arg: 'an',
    asm: 'as',
    ava: 'av',
    ave: 'ae',
    aym: 'ay',
    aze: 'az',
    bak: 'ba',
    bam: 'bm',
    bel: 'be',
    ben: 'bn',
    bis: 'bi',
    bod: 'bo',
    bos: 'bs',
    bre: 'br',
    bul: 'bg',
    cat: 'ca',
    ces: 'cs',
    cha: 'ch',
    che: 'ce',
    chu: 'cu',
    chv: 'cv',
    cor: 'kw',
    cos: 'co',
    cre: 'cr',
    cym: 'cy',
    dan: 'da',
    deu: 'de',
    div: 'dv',
    dzo: 'dz',
    ell: 'el',
    eng: 'en',
    epo: 'eo',
    est: 'et',
    eus: 'eu',
    ewe: 'ee',
    fao: 'fo',
    fas: 'fa',
    fij: 'fj',
    fin: 'fi',
    fra: 'fr',
    fry: 'fy',
    ful: 'ff',
    gla: 'gd',
    gle: 'ga',
    glg: 'gl',
    glv: 'gv',
    grn: 'gn',
    guj: 'gu',
    hat: 'ht',
    hau: 'ha',
    hbs: 'sh',
    heb: 'he',
    her: 'hz',
    hin: 'hi',
    hmo: 'ho',
    hrv: 'hr',
    hun: 'hu',
    hye: 'hy',
    ibo: 'ig',
    ido: 'io',
    iii: 'ii',
    iku: 'iu',
    ile: 'ie',
    ina: 'ia',
    ind: 'id',
    ipk: 'ik',
    isl: 'is',
    ita: 'it',
    jav: 'jv',
    jpn: 'ja',
    kal: 'kl',
    kan: 'kn',
    kas: 'ks',
    kat: 'ka',
    kau: 'kr',
    kaz: 'kk',
    khm: 'km',
    kik: 'ki',
    kin: 'rw',
    kir: 'ky',
    kom: 'kv',
    kon: 'kg',
    kor: 'ko',
    kua: 'kj',
    kur: 'ku',
    lao: 'lo',
    lat: 'la',
    lav: 'lv',
    lim: 'li',
    lin: 'ln',
    lit: 'lt',
    ltz: 'lb',
    lub: 'lu',
    lug: 'lg',
    mah: 'mh',
    mal: 'ml',
    mar: 'mr',
    mkd: 'mk',
    mlg: 'mg',
    mlt: 'mt',
    mon: 'mn',
    mri: 'mi',
    msa: 'ms',
    mya: 'my',
    nau: 'na',
    nav: 'nv',
    nbl: 'nr',
    nde: 'nd',
    ndo: 'ng',
    nep: 'ne',
    nld: 'nl',
    nno: 'nn',
    nob: 'nb',
    nor: 'no',
    nya: 'ny',
    oci: 'oc',
    oji: 'oj',
    ori: 'or',
    orm: 'om',
    oss: 'os',
    pan: 'pa',
    pli: 'pi',
    pol: 'pl',
    por: 'pt',
    pus: 'ps',
    que: 'qu',
    roh: 'rm',
    ron: 'ro',
    run: 'rn',
    rus: 'ru',
    sag: 'sg',
    san: 'sa',
    sin: 'si',
    slk: 'sk',
    slv: 'sl',
    sme: 'se',
    smo: 'sm',
    sna: 'sn',
    snd: 'sd',
    som: 'so',
    sot: 'st',
    spa: 'es',
    sqi: 'sq',
    srd: 'sc',
    srp: 'sr',
    ssw: 'ss',
    sun: 'su',
    swa: 'sw',
    swe: 'sv',
    tah: 'ty',
    tam: 'ta',
    tat: 'tt',
    tel: 'te',
    tgk: 'tg',
    tgl: 'tl',
    tha: 'th',
    tir: 'ti',
    ton: 'to',
    tsn: 'tn',
    tso: 'ts',
    tuk: 'tk',
    tur: 'tr',
    twi: 'tw',
    uig: 'ug',
    ukr: 'uk',
    urd: 'ur',
    uzb: 'uz',
    ven: 've',
    vie: 'vi',
    vol: 'vo',
    wln: 'wa',
    wol: 'wo',
    xho: 'xh',
    yid: 'yi',
    yor: 'yo',
    zha: 'za',
    zho: 'zh',
    zul: 'zu',
    gsw: 'de' // customly added
};

export function convertIso6393ToIso6391(rawLocale: ISO_639_3_LOCALE): ISO_639_1_LOCALE {
    if (!rawLocale) {
        return undefined;
    }
    return LOCALE_MAPPING[rawLocale] ?? (rawLocale.slice(0, 2) as ISO_639_1_LOCALE);
}

/**
 * For a language 'de' will return like { gsw: 'Schweizerdeutsch', deu: 'Deutsch', ... }
 * This function needs to be called only on server side because DisplayNames returns unstable results within different browsers.
 */
export function getDisplayNamesFromIso6393(language: LanguageCode): Record<ISO_639_1_LOCALE, string> {
    return Object.keys(LOCALE_MAPPING).reduce(
        (mapping, locale) => {
            mapping[locale] = new Intl.DisplayNames(language, { type: 'language' }).of(locale);
            return mapping;
        },
        {} as Record<ISO_639_1_LOCALE, string>
    );
}
/**
 * Returns the name of the location for a given locale.
 * @example
 * locale: 'en-GB'
 * returns: 'United Kingdom'
 * @param locale
 */
export function getLocationNameFromLocale(locale: Locale): string | undefined {
    return new Intl.DisplayNames(locale.replace('_', '-'), { type: 'region' }).of(
        locale.replace('_', '-').split('-')[1]
    );
}

/**
 * Splits a locale key into its language and country names, both rendered in
 * `displayLocale` — the language the UI is being viewed in. An admin browsing in
 * English reads "German" / "Switzerland" for 'de-CH'; the same key in the German
 * UI reads "Deutsch" / "Schweiz".
 *
 * Contrast getLocationNameFromLocale, which returns only the country and names it
 * in the language of the key itself rather than the viewer's.
 *
 * `country` is undefined when the key carries no region ('de'). Both parts fall
 * back to the raw subtag when the runtime does not recognise it, so a caller
 * always has something to render.
 *
 * No dependency needed: Intl.DisplayNames draws the ~250 country and ~8000
 * language names from the runtime's CLDR data, already localised — none of it
 * belongs in the translation files.
 *
 * @example
 * localeKey: 'de-CH', displayLocale: 'en-US'
 * returns: { language: 'German', country: 'Switzerland' }
 * @example
 * localeKey: 'de-CH', displayLocale: 'de-DE'
 * returns: { language: 'Deutsch', country: 'Schweiz' }
 */
export function getLocaleKeyDisplayParts(
    localeKey: string,
    displayLocale: string
): { language: string; country: string | undefined } {
    const [languageSubtag, countrySubtag] = localeKey.replace('_', '-').split('-');

    let languageNames: Intl.DisplayNames;
    let countryNames: Intl.DisplayNames;
    try {
        // 'standard' avoids dialect names ("Swiss High German") for regional keys
        languageNames = new Intl.DisplayNames([displayLocale], {
            type: 'language',
            languageDisplay: 'standard'
        });
        countryNames = new Intl.DisplayNames([displayLocale], { type: 'region' });
    } catch {
        // A structurally invalid displayLocale makes the constructor throw
        return { language: languageSubtag, country: countrySubtag };
    }

    return {
        language: languageNames.of(languageSubtag) ?? languageSubtag,
        country: countrySubtag ? (countryNames.of(countrySubtag) ?? countrySubtag) : undefined
    };
}

export const COUNTRY_TIMEZONE_MAPPING: { [key in CountryCode]?: TimeZone } = {
    DE: 'Europe/Berlin',
    CH: 'Europe/Zurich',
    AT: 'Europe/Vienna'
};

export function getCountryTimeZone(countryCode: CountryCode): TimeZone {
    return COUNTRY_TIMEZONE_MAPPING[countryCode] || 'Europe/Berlin';
}

/**
 * Safe wrapper for getting language code from string inputs
 * Handles both Locale enum and plain string values
 */
export function getLanguageCodeFromString(locale: string | false): ISO_639_1_LOCALE | undefined {
    if (!locale) return undefined;

    // If it's a valid Locale enum, use the existing function
    if (isLocale(locale)) {
        return getLanguageCodeFromLocale(locale);
    }

    // Try to extract language code directly from string
    // Handle formats like "en_GB", "en-GB", or just "en"
    const normalized = locale.replace('-', '_');
    return toISO_639_1(normalized.substring(0, 2));
}

/**
 * Safe wrapper for getting country code from string inputs
 * Handles both Locale enum and plain string values
 *
 * @deprecated Assumes the country from the language when the locale carries none (e.g. 'en' → 'US').
 * In apps whose URL locale contains the country (web: 'de-DE', 'en-US', …), derive it from the URL
 * locale via getCountryCodeFromAppLocale instead, or pass an explicit countryCode down.
 */
export function getCountryCodeFromString(locale: string | false): ISO_3166_1_LOCALE | undefined {
    if (!locale) return undefined;

    // If it's a valid Locale enum, use the existing function
    if (isLocale(locale)) {
        return getCountryCodeFromLocale(locale);
    }

    // Try to extract country code from string like "en_GB" or "en-GB"
    const parts = locale.split(/[_-]/);

    return parts[1] ? toISO_3166_1(parts[1]) : DEFAULT_COUNTRY_CODE_BY_LANGUAGE[parts[0] as SupportedLanguageCode];
}
